import { MapMouseEvent, MapTouchEvent } from 'mapbox-gl';
import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../redux';
import { updateActiveTrails } from '../redux/reducer';
import { coordinatesToSerializedCoordinates } from '../utils/serializedCoordinates';
import useMap from './useMap';

export default function useActiveTrailCoordinates(
  selectedSerializedCoordinates: string,
  setSelectedSerializedCoordinates: (serializedCoordinates: string) => void
) {
  const dispatch = useAppDispatch();
  const map = useMap();
  const { activeTrails } = useAppSelector(state => state.map);

  const activeTrailSerializedCoordinates = useMemo(() => {
    if (!activeTrails?.length) {
      return undefined;
    }
    const trail = activeTrails[0];
    if (!trail) {
      return undefined;
    }

    const coordinates = trail._cluster_geoloc ?? trail._geoloc;
    if (!coordinates) {
      return undefined;
    }

    return coordinatesToSerializedCoordinates(coordinates);
  }, [activeTrails]);

  useEffect(() => {
    if (activeTrailSerializedCoordinates !== selectedSerializedCoordinates) {
      setSelectedSerializedCoordinates('');
    }
    // If the active trail coordinates are changed by another component, clear the selected state
    // since that can remain while a user hovers over something like a trail card.
    // Don't want to trigger this when the selected coordinates change.
  }, [activeTrailSerializedCoordinates]);

  useEffect(() => {
    const onClick = (event: MapMouseEvent | MapTouchEvent) => {
      // We prevent default when a user clicks on this layer
      if (!event.defaultPrevented) {
        setSelectedSerializedCoordinates('');
        dispatch(updateActiveTrails(undefined));
      }
    };

    map?.on('click', onClick);
    return () => {
      map?.off('click', onClick);
    };
  }, [dispatch, map, setSelectedSerializedCoordinates]);

  return {
    activeTrailSerializedCoordinates
  };
}
